/**
 * BoxLabelPreview — TEK merkezi 100×100mm koli etiketi önizleme.
 *
 * Kullanıcı isteği (msg-193): İki tetik noktası (Üretim Emirleri + Bitmiş
 * Ürünler) AYNI preview modalını, AYNI backend endpoint'ini, AYNI şablonu
 * kullanır. Etikette Türkçe/karakter dönüşümü backend'de yapılır.
 *
 * Props:
 *   open           : boolean
 *   onClose        : () => void
 *   source         : "production" | "finished"
 *   productionOrderId (production ise) : string
 *   finishedLotId (finished ise) : string
 *   prodNo         : string (başlık için)
 *
 * Preview'da düzenleme YOK (madde 65-66). Sadece: YAZDIR + PDF INDIR + KAPAT.
 */
import React, { useEffect, useState, useMemo } from "react";
import { Modal, Btn } from "@/components/UI";
import { api } from "@/lib/api";
import { Printer, Download, X } from "lucide-react";

export default function BoxLabelPreview({
  open,
  onClose,
  source,
  productionOrderId,
  finishedLotId,
  prodNo,
  // [iter72] Malzeme etiket desteği (msg-93 Bölüm A/B)
  materialId,
  lotId,
  poId,
  labelKind, // "KG" | "ADET" — sadece başlık için
}) {
  const [pdfUrl, setPdfUrl] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  const endpoint = useMemo(() => {
    const base = window.location.origin;
    const bu = encodeURIComponent(base);
    if (source === "production" && productionOrderId) {
      return `/production-orders/${productionOrderId}/box-label.pdf?base_url=${bu}`;
    }
    if (source === "finished" && finishedLotId) {
      return `/finished-lots/${finishedLotId}/box-label.pdf?base_url=${bu}`;
    }
    if (source === "material" && materialId) {
      const params = [`base_url=${bu}`];
      if (lotId) params.push(`lot_id=${lotId}`);
      if (poId) params.push(`po_id=${poId}`);
      return `/material-labels/${materialId}/label.pdf?${params.join("&")}`;
    }
    return null;
  }, [source, productionOrderId, finishedLotId, materialId, lotId, poId]);

  useEffect(() => {
    if (!open || !endpoint) return;
    let alive = true;
    let currentUrl = null;
    (async () => {
      setBusy(true);
      setError(null);
      setPdfUrl(null);  // [iter86] önceki hata/pdf state'i temizle
      try {
        const blob = await api.getBlob(endpoint);
        if (!alive) return;
        // Backend hata durumunda responseType='blob' olsa bile axios throw eder;
        // ancak bazı proxy'ler 2xx + application/json döndürüp Blob içine hata
        // koyabilir. Ekstra güvenlik için blob type'ını kontrol et.
        if (blob && blob.type && !blob.type.includes("pdf")) {
          const txt = await blob.text();
          try {
            const j = JSON.parse(txt);
            throw new Error(j.detail || j.message || "Etiket oluşturulamadı");
          } catch (_) {
            throw new Error(txt || "Etiket oluşturulamadı");
          }
        }
        currentUrl = URL.createObjectURL(blob);
        setPdfUrl(currentUrl);
      } catch (e) {
        if (!alive) return;
        // axios blob response'ta e.response.data bir Blob — text'e çevirmemiz gerek
        let msg = "Etiket oluşturulamadı";
        if (e?.response?.data instanceof Blob) {
          try {
            const txt = await e.response.data.text();
            try {
              const j = JSON.parse(txt);
              msg = j.detail || j.message || txt;
            } catch { msg = txt || msg; }
          } catch { /* ignore */ }
        } else {
          msg = e?.response?.data?.detail || e?.message || msg;
        }
        setError(`${msg} (HTTP ${e?.response?.status || "?"})`);
      } finally {
        if (alive) setBusy(false);
      }
    })();
    return () => {
      alive = false;
      if (currentUrl) URL.revokeObjectURL(currentUrl);
    };
  }, [open, endpoint]);

  const doPrint = () => {
    if (!pdfUrl) return;
    // Yeni pencere aç, PDF yüklendiğinde print dialog aç.
    const w = window.open(pdfUrl, "_blank");
    if (w) {
      w.addEventListener("load", () => {
        try { w.focus(); w.print(); } catch (_) {}
      });
    }
  };

  const doDownload = () => {
    if (!pdfUrl) return;
    const a = document.createElement("a");
    a.href = pdfUrl;
    a.download = `etiket_${prodNo || "koli"}.pdf`;
    document.body.appendChild(a);
    a.click();
    a.remove();
  };

  return (
    <Modal open={open} onClose={onClose} title={
      source === "material"
        ? `Malzeme Etiketi Onizleme — ${labelKind || ""}`
        : `Koli Etiketi Önizleme — ${prodNo || ""}`
    }>
      <div data-testid="box-label-preview">
        <div className="text-[11px] text-slate-500 mb-2">
          Fiziksel boyut: 100 × 100 mm · Termal yazıcı için siyah-beyaz · ADET kutusu boş bırakılır.
        </div>

        {busy && (
          <div className="text-sm text-slate-500 py-8 text-center border rounded bg-slate-50" data-testid="box-label-loading">
            Etiket hazırlanıyor…
          </div>
        )}

        {error && !busy && (
          <div className="text-sm text-rose-800 bg-rose-50 border border-rose-200 rounded p-3" data-testid="box-label-error">
            {error}
          </div>
        )}

        {pdfUrl && !busy && !error && (
          <div className="flex justify-center bg-slate-100 rounded p-3">
            {/* Preview: 100mm'i ~378px olarak göster (madde 64 — physical size print output'ta korunur) */}
            <iframe
              title="box-label-preview"
              src={`${pdfUrl}#zoom=page-fit&toolbar=0`}
              style={{ width: 378, height: 378, background: "white", border: "1px solid #cbd5e1" }}
              data-testid="box-label-iframe"
            />
          </div>
        )}

        <div className="flex justify-end gap-2 mt-4">
          <Btn variant="secondary" onClick={onClose} data-testid="box-label-close">
            <X size={14}/> Kapat
          </Btn>
          <Btn variant="secondary" onClick={doDownload} disabled={!pdfUrl || busy} data-testid="box-label-download">
            <Download size={14}/> PDF İndir
          </Btn>
          <Btn onClick={doPrint} disabled={!pdfUrl || busy} data-testid="box-label-print">
            <Printer size={14}/> Yazdır
          </Btn>
        </div>
      </div>
    </Modal>
  );
}
